import { useCallback } from 'react';
import invariant from 'tiny-invariant';
import useDriftClient from '../../useDriftClient';
import useCurrentUserAccount from '../../useCurrentUserAccount';
import { useGetTxParams } from 'src/hooks/driftActions/transactions/useGetTxParams';
import {
	createOpenPerpNonMarketOrderIxs,
	MarketId,
	NonMarketOrderParamsConfig,
	OptionalAuctionParamsRequestInputs,
	UIMarket,
	UIOrderType,
} from '@drift/common';
import {
	BigNum,
	BN,
	DriftClient,
	PostOnlyParams,
	PRICE_PRECISION_EXP,
	MAX_LEVERAGE_ORDER_SIZE,
} from '@drift-labs/sdk';
import { ORDER_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/orderPrep';
import { PerpTradeFormOutputProps } from 'src/stores/DriftStore/actions/actionHelpers/types';
import Env, {
	syncGetCurrentSettings,
} from 'src/environmentVariables/EnvironmentVariables';
import { TRADE_PREP_UTILS } from 'src/stores/DriftStore/actions/actionHelpers/tradePrep';
import useGetOraclePriceForMarket from 'src/hooks/useGetOraclePriceForMarket';
import { notify } from 'src/utils/notifications';
import { getOrderToastMessage } from 'src/stores/DriftStore/helpers/toastHelpers';
import { generateUserOrderIdForUser } from 'src/utils/order';
import { useTxErrorHandler } from 'src/hooks/driftActions/transactions/useTxErrorHandler';
import { buildScaledPerpLimitOrderIx } from 'src/utils/scaledOrders';
import { TransactionInstruction } from '@solana/web3.js';
import { checkMaxLeverageFlagUsage } from 'src/utils/trade/checkMaxLeverageFlagUsage';
import { useAutoSignDriftActionWithFallback } from 'src/hooks/wallets/useAutoSignDriftActionWithFallback';
import { useWalletsV2 } from 'src/hooks/wallets/useWalletsV2';
import {
	UiEnhancedIWalletV2,
	TimingCallbacks,
} from 'src/utils/wallets/IWalletV2';
import { MAX_COMPUTE_UNITS } from 'src/constants/constants';
import { FeePayerError } from 'src/constants/errors/FeePayerError';
import { logNonMarketOrderTimingMetrics } from 'src/utils/logging/nonMarketOrderTiming';
import usePostHogCapture from 'src/hooks/posthog/usePostHogCapture';

const getOrderConfig = (
	orderType: UIOrderType,
	marketId: MarketId,
	primaryPrice: BN,
	secondaryPrice: BN,
	oraclePrice: BN,
	postOnly: boolean,
	driftClient: DriftClient,
	bracketOrders: ReturnType<
		typeof ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps
	>['bracketOrders'],
	optionalAuctionParamsInputs: OptionalAuctionParamsRequestInputs
) => {
	let orderConfig: NonMarketOrderParamsConfig['orderConfig'];
	switch (orderType) {
		case 'limit': {
			const settings = syncGetCurrentSettings();
			const auctionStartPriceOffset =
				TRADE_PREP_UTILS.getMarketBasedAuctionStartPriceOffset(
					settings?.auctionStartPriceOffset as number | 'marketBased',
					marketId
				);
			const enablePlaceAndTake =
				!!settings?.placeAndTakeEnabled && driftClient.txVersion === 0;

			orderConfig = {
				orderType: 'limit',
				limitPrice: primaryPrice,
				bracketOrders,
				limitAuction: {
					enable: !postOnly,
					dlobServerHttpUrl: Env.dlobServerHttpUrl,
					auctionStartPriceOffset,
					oraclePrice: oraclePrice,
					optionalLimitAuctionParams: optionalAuctionParamsInputs,
					usePlaceAndTake: {
						enable: enablePlaceAndTake,
						referrerInfo: driftClient.getUserStats()?.getReferrerInfo(),
						auctionDurationPercentage: Env.defaultAuctionDurationPercentage,
					},
				},
			};
			break;
		}
		case 'stopLimit': {
			orderConfig = {
				orderType: 'stopLoss',
				triggerPrice: primaryPrice,
				limitPrice: secondaryPrice,
			};
			break;
		}
		case 'stopMarket': {
			orderConfig = {
				orderType: 'stopLoss',
				triggerPrice: primaryPrice,
			};
			break;
		}
		case 'takeProfitLimit': {
			orderConfig = {
				orderType: 'takeProfit',
				triggerPrice: primaryPrice,
				limitPrice: secondaryPrice,
			};
			break;
		}
		case 'takeProfitMarket': {
			orderConfig = {
				orderType: 'takeProfit',
				triggerPrice: primaryPrice,
			};
			break;
		}
		case 'oracleLimit': {
			orderConfig = {
				orderType: 'oracleLimit',
				oraclePriceOffset: primaryPrice,
			};
			break;
		}
		default: {
			throw new Error(`Unsupported non-market order type: ${orderType}`);
		}
	}

	return orderConfig;
};

const notifyAwaitingWalletConfirmation = (
	toastId: string,
	isAutoSigned: boolean
) => {
	notify({
		type: isAutoSigned ? 'awaiting' : 'awaiting_wallet_confirmation',
		message: isAutoSigned ? 'Placing order...' : undefined,
		id: toastId,
		showUntilCancelled: true,
	});
};

const notifyOrderPlaced = (toastId: string, orderType: UIOrderType) => {
	const completedOrderMessage = getOrderToastMessage({
		orderType: orderType,
	});

	notify({
		type: 'success',
		message: completedOrderMessage,
		id: toastId,
		updatePrevious: true,
	});
};

export const useOpenPerpNonMarketOrder = () => {
	const driftClient = useDriftClient();
	const currentUserAccount = useCurrentUserAccount();
	const getTxParams = useGetTxParams();
	const getOraclePrice = useGetOraclePriceForMarket();
	const handleTxError = useTxErrorHandler();
	const { captureEvent } = usePostHogCapture();
	const {
		authorityWallet,
		authorityWalletNoFeePayer,
		isEmbeddedWalletThePreferredTradingSigner,
	} = useWalletsV2();

	// Inner action that performs the actual order placement
	const openPerpNonMarketOrderAction = useCallback(
		async (
			signerWallet: UiEnhancedIWalletV2,
			isAutoSigned: boolean,
			toastId: string,
			perpTradeFormOutputProps: PerpTradeFormOutputProps & {
				orderType: Exclude<UIOrderType, 'market' | 'oracle'>;
				leadSide: 'base' | 'quote';
			},
			highLeverageSpotsOpen?: number
		): Promise<string | undefined> => {
			invariant(driftClient, 'driftClient is required');
			invariant(currentUserAccount?.client, 'currentUserAccount is required');

			// Derive shared info from the trade form props (direction, size, bracket orders, etc)
			const {
				direction,
				baseAmountBigNum,
				bracketOrders,
				optionalAuctionParamsInputs,
			} = ORDER_PREP_UTILS.getExtraInfoFromPerpTradeFormOutputProps(
				{
					...perpTradeFormOutputProps,
				} as PerpTradeFormOutputProps & { leadSide: 'base' | 'quote' },
				{
					isOracleOrder: false, // we don't use oracle order parameters for limit auction orders
				}
			);
			const orderType = perpTradeFormOutputProps.orderType ?? 'limit';

			const uiMarket = UIMarket.createPerpMarket(
				perpTradeFormOutputProps.targetMarketIndex
			);
			const oraclePrice = getOraclePrice(uiMarket.marketId);

			const primaryPriceBN: BN = BigNum.fromPrint(
				perpTradeFormOutputProps.priceBoxStringValue ?? '0',
				PRICE_PRECISION_EXP
			).val;
			const secondaryPriceBN: BN = BigNum.fromPrint(
				perpTradeFormOutputProps.secondaryPriceBoxStringValue ?? '0',
				PRICE_PRECISION_EXP
			).val;

			const userOrderId = generateUserOrderIdForUser(currentUserAccount.client);
			notifyAwaitingWalletConfirmation(toastId, isAutoSigned);

			// Timing tracking for order execution
			const startTime = Date.now();
			const timingData = {
				ixCreationStartTime: null as number | null,
				ixCreationEndTime: null as number | null,
				txBuildingStartTime: null as number | null,
				txBuildingEndTime: null as number | null,
				feePayerSigningStartTime: null as number | null,
				feePayerSigningEndTime: null as number | null,
				embeddedWalletSigningStartTime: null as number | null,
				embeddedWalletSigningEndTime: null as number | null,
				sendTransactionStartTime: null as number | null,
				sendTransactionEndTime: null as number | null,
			};

			try {
				let ixs: TransactionInstruction | TransactionInstruction[];

				timingData.ixCreationStartTime = Date.now();

				if (orderType === 'scaledOrders') {
					const scaledOrders = perpTradeFormOutputProps.scaledOrders;
					invariant(scaledOrders, 'scaledOrders is required');
					invariant(
						scaledOrders.sizeDistribution,
						'sizeDistribution is required'
					);
					invariant(scaledOrders.orderCount, 'orderCount is required');

					ixs = await buildScaledPerpLimitOrderIx({
						driftClient,
						user: currentUserAccount.client,
						marketIndex: perpTradeFormOutputProps.targetMarketIndex,
						direction,
						totalBaseSize: baseAmountBigNum,
						orderCount: scaledOrders.orderCount,
						sizeDistribution: scaledOrders.sizeDistribution,
						startPrice: primaryPriceBN,
						endPrice: secondaryPriceBN,
						reduceOnly: perpTradeFormOutputProps.reduceOnly,
						postOnly: perpTradeFormOutputProps.postOnly,
						mainSignerOverride: signerWallet.publicKey,
						positionMaxLeverage: perpTradeFormOutputProps.displayLeverage,
						isolatedPositionDeposit:
							perpTradeFormOutputProps.isolatedPositionDeposit,
						additionalIsolatedPositionDeposits:
							perpTradeFormOutputProps.additionalIsolatedPositionDeposits,
					});
				} else {
					const orderConfig = getOrderConfig(
						orderType,
						uiMarket.marketId,
						primaryPriceBN,
						secondaryPriceBN,
						oraclePrice.val,
						!!perpTradeFormOutputProps.postOnly,
						driftClient,
						bracketOrders,
						optionalAuctionParamsInputs
					);

					// safety check for max leverage flag usage
					const useMaxLeverage = checkMaxLeverageFlagUsage(
						!!perpTradeFormOutputProps.maxLeverageSelected,
						perpTradeFormOutputProps.targetMarketIndex,
						baseAmountBigNum,
						oraclePrice,
						currentUserAccount.client,
						direction,
						perpTradeFormOutputProps.displayLeverage,
						highLeverageSpotsOpen
					);
					const baseAmountToUseBN = useMaxLeverage
						? MAX_LEVERAGE_ORDER_SIZE
						: baseAmountBigNum.val;

					ixs = await createOpenPerpNonMarketOrderIxs({
						driftClient,
						user: currentUserAccount.client,
						marketIndex: perpTradeFormOutputProps.targetMarketIndex,
						direction,
						amount: baseAmountToUseBN,
						assetType: 'base',
						orderConfig: orderConfig,
						reduceOnly: perpTradeFormOutputProps.reduceOnly,
						postOnly: perpTradeFormOutputProps.postOnly
							? PostOnlyParams.MUST_POST_ONLY
							: PostOnlyParams.NONE,
						userOrderId,
						mainSignerOverride: signerWallet.publicKey,
						positionMaxLeverage: perpTradeFormOutputProps.displayLeverage,
						highLeverageOptions: {
							numOfOpenHighLeverageSpots: highLeverageSpotsOpen,
						},
						isolatedPositionDepositsOverride:
							perpTradeFormOutputProps.isolatedPositionDeposit
								? {
										mainDeposit:
											perpTradeFormOutputProps.isolatedPositionDeposit,
										additionalDeposits:
											perpTradeFormOutputProps.additionalIsolatedPositionDeposits,
									}
								: undefined,
					});
				}

				timingData.ixCreationEndTime = Date.now();

				const timingCallbacks: TimingCallbacks = {
					onTxBuildingStart: () => {
						timingData.txBuildingStartTime = Date.now();
					},
					onTxBuildingEnd: () => {
						timingData.txBuildingEndTime = Date.now();
					},
					onFeePayerSigningStart: () => {
						timingData.feePayerSigningStartTime = Date.now();
					},
					onFeePayerSigningEnd: () => {
						timingData.feePayerSigningEndTime = Date.now();
					},
					onEmbeddedWalletSigningStart: () => {
						timingData.embeddedWalletSigningStartTime = Date.now();
					},
					onEmbeddedWalletSigningEnd: () => {
						timingData.embeddedWalletSigningEndTime = Date.now();
					},
					onSendTransactionStart: () => {
						timingData.sendTransactionStartTime = Date.now();
					},
					onSendTransactionEnd: () => {
						timingData.sendTransactionEndTime = Date.now();
					},
				};

				const txSig = await signerWallet.signAndSendTransaction(
					ixs,
					getTxParams({
						marketId: uiMarket.marketId,
						computeUnits:
							orderType === 'scaledOrders' ? MAX_COMPUTE_UNITS : undefined,
					}),
					timingCallbacks
				);

				if (!txSig) {
					notify({
						id: toastId,
						type: 'error',
						message: 'Failed to open order.',
						updatePrevious: true,
					});
					return undefined;
				}

				notifyOrderPlaced(toastId, orderType);

				return txSig;
			} catch (e) {
				console.error('Error opening perp non-market order', e);

				if (e instanceof FeePayerError) {
					// handled by useDriftActionWithFallback
					throw e;
				}

				// Check if this is a transaction size error with additional isolated deposits
				const hasAdditionalDeposits =
					!!perpTradeFormOutputProps.additionalIsolatedPositionDeposits?.length;
				const isTxSizeError = (e as Error)?.message?.match(
					/\(max: encoded\/raw \d+\/\d+\)/
				);

				if (hasAdditionalDeposits && isTxSizeError) {
					handleTxError(
						new Error(
							'Transaction too large due to isolated position top-ups. Please manually add margin to your under-collateralized isolated positions or reduce their size before placing this order.'
						),
						toastId,
						'Transaction Too Large'
					);
					return undefined;
				}

				handleTxError(e, toastId);

				return undefined;
			} finally {
				logNonMarketOrderTimingMetrics({
					startTime,
					timingData,
					marketIndex: perpTradeFormOutputProps.targetMarketIndex,
					orderType: orderType,
					baseAmount: baseAmountBigNum.toNum(),
					captureEvent,
				});
			}
		},
		[
			driftClient,
			currentUserAccount?.client,
			getTxParams,
			getOraclePrice,
			handleTxError,
			captureEvent,
		]
	);

	const openPerpNonMarketOrderWithAutoSign = useAutoSignDriftActionWithFallback(
		openPerpNonMarketOrderAction
	);

	// Final function that routes to authority wallet for isolated margin orders
	const openPerpNonMarketOrder = useCallback(
		async (
			toastId: string,
			perpTradeFormOutputProps: PerpTradeFormOutputProps & {
				orderType: Exclude<UIOrderType, 'market' | 'oracle'>;
				leadSide: 'base' | 'quote';
			},
			highLeverageSpotsOpen?: number
		): Promise<string | undefined> => {
			// For isolated margin orders with embedded wallet, use authority wallet directly
			// because the Privy embedded wallet + fee payer flow doesn't work for isolated position deposits
			const needsAuthorityWalletFallback =
				isEmbeddedWalletThePreferredTradingSigner &&
				!!perpTradeFormOutputProps.isolatedPositionDeposit &&
				perpTradeFormOutputProps.orderType !== 'limit';

			if (needsAuthorityWalletFallback && authorityWallet) {
				return openPerpNonMarketOrderAction(
					authorityWallet,
					false, // isAutoSigned = false, user will manually sign
					toastId,
					perpTradeFormOutputProps,
					highLeverageSpotsOpen
				);
			}

			// Scaled orders: authority pays, not fee payer service
			if (
				perpTradeFormOutputProps.orderType === 'scaledOrders' &&
				authorityWalletNoFeePayer
			) {
				return openPerpNonMarketOrderAction(
					authorityWalletNoFeePayer,
					false,
					toastId,
					perpTradeFormOutputProps,
					highLeverageSpotsOpen
				);
			}

			return openPerpNonMarketOrderWithAutoSign(
				toastId,
				perpTradeFormOutputProps,
				highLeverageSpotsOpen
			);
		},
		[
			openPerpNonMarketOrderAction,
			openPerpNonMarketOrderWithAutoSign,
			authorityWallet,
			authorityWalletNoFeePayer,
			isEmbeddedWalletThePreferredTradingSigner,
		]
	);

	return openPerpNonMarketOrder;
};
